import { KeyRound } from "lucide-react";
import { PageHeading, Section, Field, Steps, Callout, RoleBadge, Diagram, MockSelect, MockField, MockButton } from "../shared";

export default function AdminRolesPermissionsSection() {
  return (
    <div data-testid="guide-admin-roles-permissions">
      <PageHeading
        icon={KeyRound}
        title="Roles & Permissions"
        description="The master list of what every role can see and do across the app. Build custom roles, set their access, and review an audit log of every change."
      />

      <Callout variant="info" title="In plain words">
        A role is a job description that decides which screens a person can open and what they can do there. Instead of hundreds of checkboxes, each page gets one simple picker: <span className="text-white">None</span>, <span className="text-white">View</span>, <span className="text-white">Edit</span>, or <span className="text-white">Full</span>. Every change here asks for your password again and is written to an audit log.
      </Callout>

      <Section title="Who can manage it">
        <div className="flex items-center gap-2"><RoleBadge role="super_admin" /><span className="text-xs text-white/55"><span className="text-white">manage_roles_permissions</span>. Every change requires step-up re-authentication and is written to the role audit log.</span></div>
      </Section>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Use it to create a new role for a group of people, change what an existing role can do, or check who currently holds a role before changing it.
        </p>
      </Section>

      <Section title="The page-card model">
        <p className="text-xs text-white/65 leading-relaxed">Instead of ticking 30+ permission checkboxes per role, the UI groups permissions into <span className="text-white">page cards</span> — one card per navigable destination. Each card has a single access-level selector with up to four levels:</p>
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1 ml-2">
          <li><span className="text-white">None</span> — hide the page entirely.</li>
          <li><span className="text-white">View</span> — read-only access.</li>
          <li><span className="text-white">Edit</span> — typical day-to-day work (create, edit, sign-off).</li>
          <li><span className="text-white">Full</span> — adds destructive actions (delete, restore, purge).</li>
        </ul>
        <p className="text-xs text-white/55">Levels are cumulative. Some cards offer fewer levels (e.g. Roles &amp; Permissions is None or Full only). The UI groups cards into <span className="text-white">App Areas</span>, <span className="text-white">Admin Page Tabs</span>, and <span className="text-white">Settings Page Tabs</span>. Granting any non-None level inside a section auto-grants the panel-view permission so the nav-link appears.</p>
      </Section>

      <Section title="Creating a custom role">
        <Diagram
          frameLabel="Admin → Roles & Permissions → New Role"
          legend={[
            <>Click <span className="text-white">+ New Role</span> and enter its name, label, and level.</>,
            <>For each page card, pick the <span className="text-white">access level</span> (None / View / Edit / Full).</>,
            <>Click <span className="text-white">Save</span> and confirm with your password (step-up).</>,
          ]}
        >
          <MockButton label="+ New Role" marker={1} />
          <MockField label="Label" value="Reporting Viewer" />
          <MockSelect label="Summary page access" value="View" marker={2} />
          <MockSelect label="KPI Dashboard access" value="View" />
          <MockField label="Your password (step-up)" placeholder="••••••••" marker={3} />
          <MockButton label="Save" />
        </Diagram>
        <Steps steps={[
          <>Click <span className="text-white">+ New Role</span>. Enter the name, label, description, and level.</>,
          <>Set per-card access levels. The right-hand summary updates live to show what the role can do.</>,
          <>Click <span className="text-white">Save</span>. Confirm with your password (step-up). The role is written to <span className="text-white">v2_roles</span> and the bundle to <span className="text-white">v2_role_permissions</span>.</>,
          <>Assign it to users from User Accounts.</>,
        ]} />
      </Section>

      <Section title="Role fields">
        <div className="space-y-2">
          <Field name="Role name" required="required"><p>Stable internal identifier. Lowercase + underscores. Cannot collide with another active role.</p></Field>
          <Field name="Label" required="required"><p>Display name shown in dropdowns and badges.</p></Field>
          <Field name="Description" required="optional"><p>One-line explainer for other admins.</p></Field>
          <Field name="Level" required="required"><p>Hierarchy ladder used by the assign-role guard. Custom roles cap at the system <span className="text-white">MAX_CUSTOM_ROLE_LEVEL</span> (typically below super_admin) so a non-super-admin can never bootstrap themselves above their own level.</p></Field>
        </div>
      </Section>

      <Section title="Edit, delete, and audit">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1.5">
          <li>Each role row shows a <span className="text-white">user-count chip</span>. Click it to open a dialog listing every user who currently holds the role — useful before demoting or deleting it.</li>
          <li>Deleting a role soft-deletes it (sets <span className="text-white">deleted_at</span>). The name becomes reusable for a new custom role.</li>
          <li>The audit log at the bottom of the tab records every change — who did it, when, before/after state. Export to Excel for compliance reviews.</li>
        </ul>
      </Section>

      <Callout variant="warning" title="Preset roles vs system-protected">
        The eight preset roles (member, md_admin, c_suite, general_admin, hr_admin, it_admin, tech_admin, super_admin) are non-deletable — their permission bundles can be edited but the role rows themselves cannot be removed. Separately, the hidden <span className="text-white">dev_admin</span> keyholder is the only truly system-protected role: it bypasses the matrix entirely, holds every permission as a hard-coded keyholder, and is invisible from the UI.
      </Callout>
      <Callout variant="info" title="Don't strip super_admin">
        The system refuses to remove <span className="text-white">manage_roles_permissions</span> from the super_admin role — that would leave the matrix uneditable. Other restrictions cannot leave zero super-admins with the permission either.
      </Callout>
    </div>
  );
}
